import { type FormEvent, useEffect, useState } from 'react'
import { Head, router, useForm, usePage } from '@inertiajs/react'
import { ChevronUp, ChevronDown } from 'lucide-react'
import type { CompanyBoardLanesIndexProps, CompanyLaneSetting } from '@/types/company'
import type { SharedPageProps } from '@/types/shared'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { CompanyHeader } from '@/components/Shared/CompanyHeader'
import { CompanyWorkspaceNav } from '@/components/Shared/CompanyWorkspaceNav'
import { InlineErrorBanner } from '@/components/Shared/InlineErrorBanner'

// Type alias (not interface): usePage<T>'s PageProps constraint needs the
// implicit index signature only object-literal types carry.
type BoardLanesPageProps = SharedPageProps & {
  lanes: CompanyLaneSetting[]
  statusOptions: { value: string; label: string }[]
  errors?: Record<string, string>
}

export default function CompanyBoardLanesIndex({
  lanes: initialLanes,
  statusOptions,
}: CompanyBoardLanesIndexProps) {
  const { errors: pageErrors } = usePage<BoardLanesPageProps>().props
  const [orderedLanes, setOrderedLanes] = useState<CompanyLaneSetting[]>(initialLanes)
  const [renamingId, setRenamingId] = useState<number | null>(null)
  const [renameLabel, setRenameLabel] = useState('')
  const addForm = useForm({ label: '', canonical_status: '' })

  useEffect(() => setOrderedLanes(initialLanes), [initialLanes])

  function swap(index: number, direction: 'up' | 'down') {
    const neighbour = direction === 'up' ? index - 1 : index + 1
    if (neighbour < 0 || neighbour >= orderedLanes.length) {return}
    const updated = [...orderedLanes]
    ;[updated[index], updated[neighbour]] = [updated[neighbour], updated[index]]
    setOrderedLanes(updated)
    router.post('/company/board_lanes/reorder', {
      ordered_lane_ids: updated.map((l) => l.id),
    })
  }

  function startRename(lane: CompanyLaneSetting) {
    setRenamingId(lane.id)
    setRenameLabel(lane.label)
  }

  function submitRename(lane: CompanyLaneSetting) {
    router.patch(
      `/company/board_lanes/${lane.id}`,
      { lane: { label: renameLabel } },
      { onSuccess: () => setRenamingId(null) },
    )
  }

  function handleAddSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    addForm.transform((data) => ({ lane: data }))
    addForm.post('/company/board_lanes', { onSuccess: () => addForm.reset() })
  }

  return (
    <>
      <Head title="Pipeline lanes — Hyrin" />

      <div className="min-h-screen bg-muted">
        <CompanyHeader />

        <main className="mx-auto max-w-[720px] px-6 py-[52px]">
          <p className="mb-[14px] font-mono text-[11px] uppercase tracking-eyebrow text-primary">
            Board settings
          </p>
          <h1 className="mb-[10px] font-heading text-[36px] font-bold leading-[1.06] tracking-[-0.025em] text-foreground">
            Pipeline lanes
          </h1>

          <CompanyWorkspaceNav />

          <p className="mb-7 max-w-[560px] font-sans text-base leading-[1.55] text-muted-foreground">
            Customise the swimlanes on your team&apos;s hiring board. Each lane maps to one pipeline
            status.
          </p>

          <div className="rounded-[16px] bg-background p-8 shadow-sm">
            <InlineErrorBanner message={pageErrors?.base} />

            <ul className="flex flex-col divide-y divide-border">
              {orderedLanes.map((lane, index) => (
                <li key={lane.id} className="flex items-center gap-3 py-3">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      aria-label="Move lane up"
                      disabled={index === 0}
                      onClick={() => swap(index, 'up')}
                      className="h-5 w-5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronUp className="size-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="Move lane down"
                      disabled={index === orderedLanes.length - 1}
                      onClick={() => swap(index, 'down')}
                      className="h-5 w-5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronDown className="size-4" />
                    </button>
                  </div>

                  {renamingId === lane.id ? (
                    <div className="flex flex-1 items-center gap-2">
                      <Input
                        value={renameLabel}
                        onChange={(e) => setRenameLabel(e.target.value)}
                        className="h-8 text-[13px]"
                      />
                      <Button type="button" size="sm" onClick={() => submitRename(lane)}>
                        Save
                      </Button>
                      <button
                        type="button"
                        onClick={() => setRenamingId(null)}
                        className="font-sans text-[12px] text-muted-foreground hover:text-foreground"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-1 items-center gap-3">
                      <span className="font-sans text-[14px] text-foreground">{lane.label}</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                        {lane.canonicalStatus}
                      </span>
                      <button
                        type="button"
                        onClick={() => startRename(lane)}
                        className="ml-auto font-sans text-[12px] text-primary hover:opacity-80"
                      >
                        Rename
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <div className="mt-6 border-t border-border pt-6">
              <p className="mb-4 font-sans text-[13px] font-semibold text-foreground">Add a lane</p>
              <form onSubmit={handleAddSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-[6px]">
                  <label
                    htmlFor="lane-label"
                    className="font-sans text-[13px] font-medium text-foreground"
                  >
                    Label
                  </label>
                  <Input
                    id="lane-label"
                    type="text"
                    value={addForm.data.label}
                    onChange={(e) => addForm.setData('label', e.target.value)}
                    placeholder="Technical interview"
                    disabled={addForm.processing}
                  />
                  {addForm.errors.label && (
                    <p className="font-sans text-[12px] text-destructive">{addForm.errors.label}</p>
                  )}
                </div>

                <div className="flex flex-col gap-[6px]">
                  <label
                    htmlFor="lane-status"
                    className="font-sans text-[13px] font-medium text-foreground"
                  >
                    Maps to status
                  </label>
                  <select
                    id="lane-status"
                    value={addForm.data.canonical_status}
                    onChange={(e) => addForm.setData('canonical_status', e.target.value)}
                    disabled={addForm.processing}
                    className="h-9 rounded-md border border-input bg-background px-3 font-sans text-[13px] text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Select a status…</option>
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {addForm.errors.canonical_status && (
                    <p className="font-sans text-[12px] text-destructive">
                      {addForm.errors.canonical_status}
                    </p>
                  )}
                </div>

                <div className="flex justify-end pt-2">
                  <Button type="submit" disabled={addForm.processing}>
                    {addForm.processing ? 'Adding…' : 'Add lane'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
